/**
 * EventResults Component - Redesigned to match NHCA results sheet aesthetic
 * Supports URL parameters for direct linking: /results?hill=Harewood&date=2025-09-30
 * Layout matches professional results sheet with Top Ten, Records, Class Results, and All Runs
 *
 * Split/speed column labels are derived from the NHCA JasperReports XML definitions,
 * which conditionally render named timing points per hill.
 *
 * API run field names (verified against public.times schema):
 *   launch_time    → Runlaunchtime   (64ft / launch trap)
 *   split1–split5  → Runsplit1–5     (intermediate splits)
 *   speed1–speed4  → Runspeed1–4     (speed traps; only 1 & 2 used by current hills)
 *   time           → Runfinishtime   (finish time)
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select from 'react-select';

// ---------------------------------------------------------------------------
// Hill-specific timing column definitions
// Derived from the printWhenExpression column header and detail band frames
// in the NHCA JasperReports XML.
//
// Each entry is an ordered array of { label, field } objects describing the
// intermediate timing columns that appear *between* the Run label and the
// Finish time.  The Finish column is always appended automatically.
// ---------------------------------------------------------------------------
const HILL_COLUMNS = {
  Harewood: [
    { label: '64ft',    field: 'launch_time' },
    { label: 'Esses in', field: 'split1' },
    { label: 'Country', field: 'split2' },
    { label: 'Orchard', field: 'split3' },
    { label: 'FH Out',  field: 'split4' },
  ],
  Prescott: [
    { label: '64ft',    field: 'launch_time' },
    { label: 'Bridge',  field: 'speed1' },
    { label: 'Ettores', field: 'split1' },
    { label: 'Pardon',  field: 'split2' },
    { label: 'Midway',  field: 'split3' },
    { label: 'Esses',   field: 'split4' },
    { label: 'Semi',    field: 'split5' },
  ],
  Shelsley: [
    { label: '64ft',     field: 'launch_time' },
    { label: 'Kennel',   field: 'split1' },
    { label: 'Crossing', field: 'split2' },
    { label: 'Ess App',  field: 'speed1' },
    { label: 'Ess Sctr', field: 'split3' },
    { label: 'Speed',    field: 'speed2' },
  ],
  Gurston: [
    { label: '64ft',   field: 'launch_time' },
    { label: 'Hollow', field: 'speed1' },
    { label: 'S1',     field: 'split1' },
    { label: 'Int.',   field: 'split2' },
    { label: 'K-A',    field: 'split3' },
    { label: 'Speed',  field: 'speed2' },
  ],
  Wiscombe: [
    // The Jasper report maps the launch trap field to "Bridge" at Wiscombe
    { label: 'Bridge', field: 'launch_time' },
    { label: 'Esses',  field: 'split1' },
  ],
  'Loton Park': [
    { label: '64ft',         field: 'launch_time' },
    { label: 'Loggerheads',  field: 'split1' },
    { label: 'Lake',         field: 'speed1' },
    { label: 'Triangle',     field: 'split2' },
    { label: 'Keepers',      field: 'split3' },
    { label: 'Cedar',        field: 'split4' },
    { label: 'Speed',        field: 'speed2' },
    { label: 'Museum',       field: 'split5' },
  ],
  Castle: [
    { label: '64ft',   field: 'launch_time' },
    { label: 'Split 1', field: 'split1' },
    { label: 'Speed',  field: 'speed1' },
  ],
  Curborough: [
    { label: '64ft',  field: 'launch_time' },
    { label: 'Speed', field: 'speed1' },
  ],
  'Curborough X': [
    { label: '64ft', field: 'launch_time' },
  ],
  Barbon: [
    { label: '64ft', field: 'launch_time' },
  ],
  'Scarborough C': [
    { label: '64ft',    field: 'launch_time' },
    { label: 'Split 1', field: 'split1' },
  ],
  'Scarborough B': [
    { label: '64ft',    field: 'launch_time' },
    { label: 'Split 1', field: 'split1' },
  ],
  // Manor Farm E labels the launch trap "Pole" rather than "64ft"
  'Manor Farm E': [
    { label: 'Pole', field: 'launch_time' },
  ],
  // Hartland variants label the launch trap "Pub"
  'Hartland B': [
    { label: 'Pub', field: 'launch_time' },
  ],
  'Hartland D': [
    { label: 'Pub', field: 'launch_time' },
  ],
  // Hartland C and Weston Park only record finish times
  'Hartland C':   [],
  'Weston Park':  [],
  Tregrehan: [
    { label: '64ft',  field: 'launch_time' },
    { label: 'Speed', field: 'speed1' },
  ],
};

/**
 * Return the column definitions for a given hill name.
 * Falls back to an empty array (finish-only) for unknown hills.
 */
const getHillColumns = (hill) => HILL_COLUMNS[hill] ?? [];

// ---------------------------------------------------------------------------

function EventResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  // Selector state (used when URL params are absent)
  const [years, setYears] = useState([]);
  const [hills, setHills] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedHill, setSelectedHill] = useState(null);
  const [selectorLoading, setSelectorLoading] = useState({ years: false, hills: false, events: false });

  // Get hill, date and vc from URL params
  const hillParam = searchParams.get('hill');
  const dateParam = searchParams.get('date');
  const vcParam   = searchParams.get('vc') === 'true';
  const rlParam   = searchParams.get('rl') === 'true';
  const feParam   = searchParams.get('fe') === 'true';

  const FILTERS = ['vc', 'rl', 'fe'];
  const toggleFilter = (key) => {
    const next = new URLSearchParams(searchParams);
    if (next.get(key) === 'true') {
      next.delete(key);
    } else {
      FILTERS.forEach(f => next.delete(f));
      next.set(key, 'true');
    }
    setSearchParams(next);
  };

  /**
   * Fetch event results from API
   */
  const fetchEventResults = async (hill, date) => {
    if (!hill || !date) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const vcSuffix = vcParam ? '&vc=true' : '';
      const rlSuffix = rlParam ? '&rl=true' : '';
      const feSuffix = feParam ? '&fe=true' : '';
      const url = `/api/event-results?hill=${encodeURIComponent(hill)}&date=${date}${vcSuffix}${rlSuffix}${feSuffix}`;

      const response = await fetch(url, {
        headers: {
          'X-API-Key': import.meta.env.VITE_API_KEY || ''
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch event results');
      }

      const data = await response.json();
      console.log('Event Results Data:', data);
      console.log('Top Ten Results:', data.topten_results);
      setResults(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching event results:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch results when URL params change
  useEffect(() => {
    if (hillParam && dateParam) {
      fetchEventResults(hillParam, dateParam);
    }
  }, [hillParam, dateParam, vcParam, rlParam, feParam]);

  // Load years when no URL params are present
  useEffect(() => {
    if (!hillParam || !dateParam) {
      setSelectorLoading(s => ({ ...s, years: true }));
      fetch('/api/event-years', { headers: { 'X-API-Key': import.meta.env.VITE_API_KEY || '' } })
        .then(r => r.json())
        .then(data => setYears(Array.isArray(data) ? data : []))
        .catch(() => setYears([]))
        .finally(() => setSelectorLoading(s => ({ ...s, years: false })));
    }
  }, [hillParam, dateParam]);

  const handleYearSelect = (option) => {
    setSelectedYear(option ? option.value : null);
    setSelectedHill(null);
    setHills([]);
    setEvents([]);
    if (!option) return;
    setSelectorLoading(s => ({ ...s, hills: true }));
    fetch(`/api/event-hills?year=${option.value}`, { headers: { 'X-API-Key': import.meta.env.VITE_API_KEY || '' } })
      .then(r => r.json())
      .then(data => setHills(Array.isArray(data) ? data : []))
      .catch(() => setHills([]))
      .finally(() => setSelectorLoading(s => ({ ...s, hills: false })));
  };

  const handleHillSelect = (option) => {
    setSelectedHill(option ? option.value : null);
    setEvents([]);
    if (!option || !selectedYear) return;
    setSelectorLoading(s => ({ ...s, events: true }));
    fetch(`/api/event-dates?year=${selectedYear}&hill=${encodeURIComponent(option.value)}`, { headers: { 'X-API-Key': import.meta.env.VITE_API_KEY || '' } })
      .then(r => r.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]))
      .finally(() => setSelectorLoading(s => ({ ...s, events: false })));
  };

  const handleEventSelect = (option) => {
    if (!option) return;
    setSearchParams({ hill: selectedHill, date: option.value });
  };

  /**
   * Format time for display
   */
  const formatTime = (time) => {
    if (!time) return '-';
    const numTime = parseFloat(time);
    if (isNaN(numTime)) return '-';
    return numTime.toFixed(2);
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  /**
   * Sort records by class order
   * Order: 250, 250C, 250V, 350, 350C, 350V, 500, 500C, 500V, etc., then Sidecar, Trike
   */
  const sortRecordsByClass = (records) => {
    const classOrder = {
      '250': 1,  '250C': 2,  '250V': 3,
      '350': 4,  '350C': 5,  '350V': 6,
      '500': 7,  '500C': 8,  '500V': 9,
      '750': 10, '750C': 11, '750V': 12,
      '1000': 13, '1000C': 14, '1000V': 15,
      '1300': 16, '1300C': 17, '1300V': 18,
      '1400': 19, '1400C': 20, '1400V': 21,
      'Sidecar': 100, 'SidecarC': 101,
      'Trike': 200,
      'FEUNDER400': 300, 'FEOVER400': 301, 'FESIDECAR': 302
    };

    return [...records].sort((a, b) => {
      const orderA = classOrder[a.class] || 999;
      const orderB = classOrder[b.class] || 999;
      return orderA - orderB;
    });
  };

  /**
   * Build a human-readable label for a run type + number combination
   */
  const buildRunLabel = (run) => {
    const runType = (run.run_type || '').toLowerCase();
    const runNum  = run.run_number || '';

    if (runType.includes('practice'))               return `Practice ${runNum}`.trim();
    if (runType.includes('timed'))                  return `Timed ${runNum}`.trim();
    if (runType.includes('topten') || runType.includes('top ten')) return `Top ten ${runNum}`.trim();
    if (runType.includes('open'))                   return `Open ${runNum}`.trim();
    return run.run_type ? `${run.run_type} ${runNum}`.trim() : `Run ${runNum}`.trim();
  };

  /**
   * Generate shareable URL
   */
  const getShareableUrl = () => {
    if (!results) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/results?hill=${encodeURIComponent(hillParam)}&date=${dateParam}`;
  };

  /**
   * Copy URL to clipboard
   */
  const copyShareUrl = () => {
    navigator.clipboard.writeText(getShareableUrl());
    alert('Results URL copied to clipboard!');
  };

  // ---------------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="event-results-sheet">
        <div className="loading-message">Loading event results...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="event-results-sheet">
        <div className="error-message">⚠️ {error}</div>
      </div>
    );
  }

  if (!results) {
    const yearOptions = years.map(y => ({ value: y, label: String(y) }));
    const hillOptions = hills.map(h => ({ value: h, label: h }));
    const eventOptions = events.map(e => ({
      value: e.date,
      label: e.event_name || formatDate(e.date)
    }));

    const selectStyles = {
      control: (base) => ({ ...base, minHeight: '46px', fontSize: '15px' }),
      menu:    (base) => ({ ...base, zIndex: 9999 }),
    };

    return (
      <div className="event-results-sheet">
        <div className="event-selector">
          <h2>Find Event Results</h2>
          <p className="event-selector-hint">
            Select a year, hill, and event to view the full results sheet.
          </p>

          <div className="selector-steps">
            <div className="selector-step">
              <label className="selector-label">Year</label>
              <Select
                options={yearOptions}
                value={selectedYear ? { value: selectedYear, label: String(selectedYear) } : null}
                onChange={handleYearSelect}
                isLoading={selectorLoading.years}
                isClearable
                placeholder="Select year..."
                styles={selectStyles}
                classNamePrefix="sel"
              />
            </div>

            <div className="selector-step">
              <label className="selector-label">Hill</label>
              <Select
                options={hillOptions}
                value={selectedHill ? { value: selectedHill, label: selectedHill } : null}
                onChange={handleHillSelect}
                isLoading={selectorLoading.hills}
                isDisabled={!selectedYear}
                isClearable
                placeholder={selectedYear ? 'Select hill...' : 'Select a year first'}
                styles={selectStyles}
                classNamePrefix="sel"
              />
            </div>

            <div className="selector-step">
              <label className="selector-label">Event</label>
              <Select
                options={eventOptions}
                onChange={handleEventSelect}
                isLoading={selectorLoading.events}
                isDisabled={!selectedHill}
                placeholder={selectedHill ? 'Select event...' : 'Select a hill first'}
                styles={selectStyles}
                classNamePrefix="sel"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------

  const FE_CLASSES = ['FEUNDER400', 'FEOVER400', 'FESIDECAR'];
  const sortedRecords = sortRecordsByClass(
    (results.records || []).filter(r => {
      if (vcParam) return /[CV]$/.test(r.class);
      if (feParam) return FE_CLASSES.includes(r.class);
      return true;
    })
  );

  // Resolve the timing columns for the current hill
  const hillColumns = getHillColumns(hillParam);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="event-results-sheet" data-proof-ready="true">

      {/* ---- Header ---- */}
      <div className="results-header">
        <div className="header-image">
          {/* Placeholder for event photo */}
        </div>
        <div className="header-content">
          <h1 className="event-title">{results.event_info.event_name || 'Event Results'}</h1>
          <p className="event-date">{results.event_info.hill} {formatDate(results.event_info.date)}</p>

          <div className="event-conditions">
            {results.event_info.weather && (
              <p><strong>Weather:</strong> {results.event_info.weather}</p>
            )}
            {results.event_info.track_condition && (
              <p><strong>Track Condition:</strong> {results.event_info.track_condition}</p>
            )}
            {results.fastest_time && (
              <p>
                <strong>FTD:</strong>{' '}
                {results.fastest_time.firstname} {results.fastest_time.lastname}{' '}
                {formatTime(results.fastest_time.time)}
              </p>
            )}
          </div>

          <button className="share-button" onClick={copyShareUrl}>
            📋 Copy Results Link
          </button>
        </div>
      </div>

      {/* ---- View filters ---- */}
      {(results.event_info?.vc || results.event_info?.roadlegal || results.event_info?.forgotten_era) && (
        <div className="results-filters">
          {results.event_info?.vc && (
            <button
              className={`filter-btn${vcParam ? ' filter-btn--active' : ''}`}
              onClick={() => toggleFilter('vc')}
            >
              Vintage &amp; Classic Only
            </button>
          )}
          {results.event_info?.roadlegal && (
            <button
              className={`filter-btn${rlParam ? ' filter-btn--active' : ''}`}
              onClick={() => toggleFilter('rl')}
            >
              Road Legal Only
            </button>
          )}
          {results.event_info?.forgotten_era && (
            <button
              className={`filter-btn${feParam ? ' filter-btn--active' : ''}`}
              onClick={() => toggleFilter('fe')}
            >
              Forgotten Era Only
            </button>
          )}
        </div>
      )}

      {/* ---- Top Ten + Records ---- */}
      <div className="results-grid-top">

        {!vcParam && !rlParam && !feParam && <div className="section top-ten-section">
          <h2>Top Ten</h2>
          <table className="results-table">
            <thead>
              <tr>
                <th>Rider</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {results.topten_results.length === 0 ? (
                <tr><td colSpan="2">No Top Ten results</td></tr>
              ) : (
                results.topten_results.map((result, idx) => {
                  const pax = [result.passenger_firstname, result.passenger_lastname].filter(Boolean).join(' ');
                  return (
                    <tr key={idx}>
                      <td>
                        {result.firstname} {result.lastname}
                        {pax && <span className="passenger-name"> & {pax}</span>}
                      </td>
                      <td className="time-cell">{formatTime(result.time)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>}

        {!rlParam && <div className="section records-section">
          <h2>Records</h2>
          <table className="results-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Date</th>
                <th>Rider</th>
                <th>Record</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.length === 0 ? (
                <tr><td colSpan="4">No records</td></tr>
              ) : (
                sortedRecords.map((record, idx) => {
                  const pax = [record.passenger_firstname, record.passenger_lastname].filter(Boolean).join(' ');
                  return (
                    <tr key={idx}>
                      <td className="class-cell">{record.class}</td>
                      <td className="date-cell">
                        {new Date(record.record_date).toLocaleDateString('en-GB', {
                          day: '2-digit', month: '2-digit', year: 'numeric'
                        })}
                      </td>
                      <td>
                        {record.firstname} {record.lastname}
                        {pax && <span className="passenger-name"> & {pax}</span>}
                      </td>
                      <td className="time-cell">{formatTime(record.record)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>}

      </div>

      {/* ---- Class Results ---- */}
      {!vcParam && !rlParam && !feParam && (
      <div className="class-results-section">
        <h2>Class Results</h2>
        {['250', '350', '500', '750', '1400', 'Sidecar', 'Trike', 'Morgan'].map(className => {
          const classData = results.class_results[className];
          if (!classData || classData.length === 0) return null;
          const showOverall = className !== 'Morgan';

          return (
            <div key={className} className="class-group">
              <div className="class-header">
                <span className="class-badge">{className}</span>
              </div>
              <table className="class-table">
                <thead>
                  <tr>
                    <th>Rider</th>
                    <th>Best</th>
                    <th>Posn</th>
                    {showOverall && <th>Overall</th>}
                  </tr>
                </thead>
                <tbody>
                  {classData.map((rider, idx) => {
                    const pax = [rider.passenger_firstname, rider.passenger_lastname].filter(Boolean).join(' ');
                    return (
                      <tr key={idx}>
                        <td>
                          <span className="race-number">{rider.race_number}</span>{' '}
                          {rider.firstname} {rider.lastname}
                          {pax && <span className="passenger-name"> & {pax}</span>}
                        </td>
                        <td className="time-cell">{formatTime(rider.best_time)}</td>
                        <td className="position-cell">{rider.class_position}</td>
                        {showOverall && <td className="position-cell">{rider.overall_position}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
      )}

      {/* ---- VC Results ---- */}
      {results.vc_results !== undefined && !results.vc_targets_available && (
        <div className="vc-results-section">
          <h2>Vintage &amp; Classic Results</h2>
          <p className="vc-no-targets-warning">No VC target times are configured for this hill — results cannot be calculated.</p>
        </div>
      )}
      {results.vc_results && results.vc_results.length > 0 && (() => {
        // Build per-rider groups preserving rank order
        const vcByRider = [];
        const seen = new Set();
        for (const row of results.vc_results) {
          const key = `${row.rider_first}|${row.rider_last}`;
          if (!seen.has(key)) {
            seen.add(key);
            const allRiderRuns = results.vc_results.filter(r =>
              r.rider_first === row.rider_first &&
              r.rider_last  === row.rider_last
            );
            const canonicalRun = allRiderRuns.find(r =>
              r.race_number === row.canonical_race_number &&
              r.cap_class   === row.canonical_cap_class
            );
            vcByRider.push({
              vc_rank:            row.vc_rank,
              race_number:        row.canonical_race_number,
              cap_class:          row.canonical_cap_class,
              rider_first:        row.rider_first,
              rider_last:         row.rider_last,
              passenger_first:    row.passenger_first,
              passenger_last:     row.passenger_last,
              target:             canonicalRun ? canonicalRun.target : row.target,
              runs:               allRiderRuns,
            });
          }
        }
        const bestRun = (rider) =>
          Math.min(...rider.runs.filter(r => r.run_type === 'Timed').map(r => r.adjusted_time));

        return (
          <div className="vc-results-section">
            <h2>Vintage &amp; Classic Results</h2>

            {/* Summary ranking table */}
            <table className="results-table vc-results-table">
              <thead>
                <tr>
                  <th style={{textAlign:'center'}}>Rank</th>
                  <th>No.</th>
                  <th>Rider</th>
                  <th>Class</th>
                  <th style={{textAlign:'right'}}>Best Run</th>
                  <th style={{textAlign:'right'}}>Target</th>
                  <th style={{textAlign:'right'}}>Adjusted</th>
                </tr>
              </thead>
              <tbody>
                {vcByRider.map((rider, idx) => (
                  <tr key={idx}>
                    <td className="position-cell">{rider.vc_rank}</td>
                    <td className="race-number">{rider.race_number}</td>
                    <td>
                      {rider.rider_first} {rider.rider_last}
                      {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ') &&
                        <span className="passenger-name"> &amp; {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ')}</span>}
                    </td>
                    <td className="class-cell">{rider.cap_class}</td>
                    <td className="time-cell">{formatTime(Math.min(...rider.runs.filter(r => r.run_type === 'Timed' && r.race_number === rider.race_number && r.cap_class === rider.cap_class).map(r => r.run_time)))}</td>
                    <td className="time-cell">{formatTime(rider.target)}</td>
                    <td className="time-cell">{formatTime(bestRun(rider))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Per-rider run detail */}
            <div className="vc-runs-detail">
              {[...vcByRider].sort((a, b) => parseInt(a.race_number) - parseInt(b.race_number)).map((rider, idx) => {
                // Group runs by race_number+cap_class so each bike/class combo gets its own block
                const bikeGroups = [];
                const bikeSeen = new Set();
                for (const run of rider.runs) {
                  const bikeKey = `${run.race_number}|${run.cap_class}`;
                  if (!bikeSeen.has(bikeKey)) {
                    bikeSeen.add(bikeKey);
                    bikeGroups.push({
                      race_number: run.race_number,
                      cap_class:   run.cap_class,
                      runs: rider.runs.filter(r => r.race_number === run.race_number && r.cap_class === run.cap_class),
                    });
                  }
                }
                return (
                  <div key={idx} className="rider-runs-group">
                    {bikeGroups.map((bike, bikeIdx) => (
                      <div key={bikeIdx}>
                        <div className="rider-header">
                          <span className="rider-number">{bike.race_number}</span>
                          <span className="rider-name">
                            {rider.rider_first} {rider.rider_last}
                            {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ') &&
                              <span className="passenger-name"> &amp; {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ')}</span>}
                          </span>
                          <span className="rider-best">{bike.cap_class}</span>
                        </div>
                        <table className="runs-table">
                          <thead>
                            <tr>
                              <th>Run</th>
                              {hillColumns.map(col => <th key={col.field}>{col.label}</th>)}
                              <th>Finish</th>
                              <th>Target</th>
                              <th>Adjusted</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bike.runs.map((run, runIdx) => (
                              <tr key={runIdx} className={run.run_type === 'Timed' && run.adjusted_time === bestRun(rider) ? 'best-run' : ''}>
                                <td className="run-type-cell">{buildRunLabel(run)}</td>
                                {hillColumns.map(col => <td key={col.field} className="split-time-cell">{formatTime(run[col.field])}</td>)}
                                <td className="finish-time-cell">{formatTime(run.run_time)}</td>
                                <td className="split-time-cell">{formatTime(run.target)}</td>
                                <td className="finish-time-cell">{formatTime(run.adjusted_time)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ---- RL Results ---- */}
      {results.rl_results && results.rl_results.length > 0 && (() => {
        // Build per-rider groups in rank order for summary, then re-sort by race number for detail
        const rlByRider = [];
        const seen = new Set();
        for (const row of results.rl_results) {
          const key = `${row.rider_first}|${row.rider_last}`;
          if (!seen.has(key)) {
            seen.add(key);
            rlByRider.push({
              rl_rank:        row.rl_rank,
              race_number:    row.race_number,
              rider_first:    row.rider_first,
              rider_last:     row.rider_last,
              passenger_first: row.passenger_first,
              passenger_last:  row.passenger_last,
              best_time:      row.best_time,
              runs: results.rl_results.filter(r =>
                r.rider_first === row.rider_first && r.rider_last === row.rider_last
              ),
            });
          }
        }

        return (
          <div className="vc-results-section">
            <h2>Road Legal Results</h2>

            {/* Summary ranking table */}
            <table className="results-table vc-results-table">
              <thead>
                <tr>
                  <th style={{textAlign:'center'}}>Rank</th>
                  <th>No.</th>
                  <th>Rider</th>
                  <th style={{textAlign:'right'}}>Best Time</th>
                </tr>
              </thead>
              <tbody>
                {rlByRider.map((rider, idx) => (
                  <tr key={idx}>
                    <td className="position-cell">{rider.rl_rank}</td>
                    <td className="race-number">{rider.race_number}</td>
                    <td>
                      {rider.rider_first} {rider.rider_last}
                      {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ') &&
                        <span className="passenger-name"> &amp; {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ')}</span>}
                    </td>
                    <td className="time-cell">{formatTime(rider.best_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Per-rider run detail in race number order */}
            <div className="vc-runs-detail">
              {[...rlByRider].sort((a, b) => parseInt(a.race_number) - parseInt(b.race_number)).map((rider, idx) => (
                <div key={idx} className="rider-runs-group">
                  <div className="rider-header">
                    <span className="rider-number">{rider.race_number}</span>
                    <span className="rider-name">
                      {rider.rider_first} {rider.rider_last}
                      {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ') &&
                        <span className="passenger-name"> &amp; {[rider.passenger_first, rider.passenger_last].filter(Boolean).join(' ')}</span>}
                    </span>
                  </div>
                  <table className="runs-table">
                    <thead>
                      <tr>
                        <th>Run</th>
                        {hillColumns.map(col => <th key={col.field}>{col.label}</th>)}
                        <th>Finish</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rider.runs.map((run, runIdx) => (
                        <tr key={runIdx} className={run.run_type === 'Timed' && run.run_time === rider.best_time ? 'best-run' : ''}>
                          <td className="run-type-cell">{buildRunLabel(run)}</td>
                          {hillColumns.map(col => <td key={col.field} className="split-time-cell">{formatTime(run[col.field])}</td>)}
                          <td className="finish-time-cell">{formatTime(run.run_time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ---- FE Results ---- */}
      {results.fe_results && results.fe_results.length > 0 && (() => {
        // Helper: dedupe by rider preserving order, return sorted copy
        const byRider = (rows, sortKey) => {
          const seen = new Set();
          const list = [];
          for (const r of rows) {
            const k = `${r.rider_first}|${r.rider_last}`;
            if (!seen.has(k)) { seen.add(k); list.push(r); }
          }
          return [...list].filter(r => r[sortKey] != null).sort((a, b) => a[sortKey] - b[sortKey]);
        };
        const paxSpan = (r) => {
          const pax = [r.passenger_first, r.passenger_last].filter(Boolean).join(' ');
          return pax ? <span className="passenger-name"> &amp; {pax}</span> : null;
        };
        const rankTable = (title, rows, rankField, timeField) => {
          if (!rows.length) return null;
          const riders = byRider(rows, rankField);
          return (
            <div style={{marginBottom:'1.5rem'}}>
              <h3 style={{padding:'8px 0 4px',borderBottom:'2px solid var(--border-color)'}}>{title}</h3>
              <table className="results-table vc-results-table">
                <thead>
                  <tr>
                    <th style={{textAlign:'center'}}>Rank</th>
                    <th>No.</th>
                    <th>Rider</th>
                    <th style={{textAlign:'right'}}>Best Time</th>
                  </tr>
                </thead>
                <tbody>
                  {riders.map((r, i) => (
                    <tr key={i}>
                      <td className="position-cell">{r[rankField]}</td>
                      <td className="race-number">{r.race_number}</td>
                      <td>{r.rider_first} {r.rider_last}{paxSpan(r)}</td>
                      <td className="time-cell">{formatTime(r[timeField])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        };

        const allRuns    = results.fe_results;
        const over400    = allRuns.filter(r => r.fe_class === 'FEOVER400');
        const under400   = allRuns.filter(r => r.fe_class === 'FEUNDER400');
        const sidecar    = allRuns.filter(r => r.fe_class === 'FESIDECAR');

        // All riders in race number order for the detail section
        const riderMap = new Map();
        for (const run of allRuns) {
          const k = `${run.rider_first}|${run.rider_last}`;
          if (!riderMap.has(k)) riderMap.set(k, { meta: run, runs: [] });
          riderMap.get(k).runs.push(run);
        }
        const detailRiders = [...riderMap.values()]
          .sort((a, b) => parseInt(a.meta.race_number) - parseInt(b.meta.race_number));

        return (
          <div className="vc-results-section">
            <h2>Forgotten Era Results</h2>
            {rankTable('Overall', allRuns, 'overall_rank', 'best_overall_time')}
            {rankTable('Over 400cc', over400, 'class_rank', 'best_class_time')}
            {rankTable('Under 400cc', under400, 'class_rank', 'best_class_time')}
            {rankTable('Sidecar', sidecar, 'class_rank', 'best_class_time')}

            <div className="vc-runs-detail">
              {detailRiders.map(({ meta, runs }, idx) => (
                <div key={idx} className="rider-runs-group">
                  <div className="rider-header">
                    <span className="rider-number">{meta.race_number}</span>
                    <span className="rider-name">
                      {meta.rider_first} {meta.rider_last}{paxSpan(meta)}
                    </span>
                  </div>
                  <table className="runs-table">
                    <thead>
                      <tr>
                        <th>Run</th>
                        {hillColumns.map(col => <th key={col.field}>{col.label}</th>)}
                        <th>Finish</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runs.map((run, ri) => (
                        <tr key={ri} className={run.run_type === 'Timed' && run.run_time === meta.best_overall_time ? 'best-run' : ''}>
                          <td className="run-type-cell">{buildRunLabel(run)}</td>
                          {hillColumns.map(col => <td key={col.field} className="split-time-cell">{formatTime(run[col.field])}</td>)}
                          <td className="finish-time-cell">{formatTime(run.run_time)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ---- All Runs ---- */}
      {!vcParam && !rlParam && !feParam && <div className="all-runs-section">
        <h2>All Runs</h2>

        {[...results.all_runs]
          .sort((a, b) => {
            const num = (riderData) =>
              riderData.runs?.length ? parseInt(riderData.runs[0].race_number) || 999999 : 999999;
            return num(a) - num(b);
          })
          .map((riderData, idx) => {
            const raceNumber = riderData.runs?.[0]?.race_number ?? '';

            return (
              <div key={idx} className="rider-runs-group">

                <div className="rider-header">
                  <span className="rider-number">{raceNumber}</span>
                  <span className="rider-name">
                    {riderData.rider.firstname} {riderData.rider.lastname}
                    {(() => {
                      const pax = [riderData.rider.passenger_firstname, riderData.rider.passenger_lastname].filter(Boolean).join(' ');
                      return pax ? <span className="passenger-name"> & {pax}</span> : null;
                    })()}
                  </span>
                  {riderData.best_time && (
                    <span className="rider-best">{formatTime(riderData.best_time)}</span>
                  )}
                </div>

                <table className="runs-table">
                  <thead>
                    <tr>
                      <th>Run</th>
                      {hillColumns.map((col) => (
                        <th key={col.field}>{col.label}</th>
                      ))}
                      <th>Finish</th>
                    </tr>
                  </thead>
                  <tbody>
                    {riderData.runs.map((run, runIdx) => (
                      <tr key={runIdx} className={run.flagged ? 'flagged-run' : ''}>
                        <td className="run-type-cell">{buildRunLabel(run)}</td>
                        {hillColumns.map((col) => (
                          <td key={col.field} className="split-time-cell">
                            {formatTime(run[col.field])}
                          </td>
                        ))}
                        <td className="finish-time-cell">{run.flagged ? 'F' : formatTime(run.time)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

              </div>
            );
          })}
      </div>}

    </div>
  );
}

export default EventResults;
